import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { CuttysarkLiveView } from '@cortex/domain-product-innovation-lab-types';
import { flowingComments, revealFocus } from './stage-helpers.js';
import { createStageSound, type StageSound } from './stage-sound.js';
import { CandidateGrid, CandidateSpotlight } from './StageCandidates.js';
import {
  ChampionFlash,
  ChampionSpotlights,
  CommentStream,
  Confetti,
  StageBackdrop,
} from './StageEffects.js';
import {
  CHAMPION_DRUMROLL_MS,
  DRUMROLL_MS,
  RankReveal,
  WinnersPodium,
  type RevealStepName,
} from './StageResults.js';

/** 紙吹雪を出しておく時間（ミリ秒）。 @graph-connects none */
const CONFETTI_MS = 7000;
/** 1 位の発表で降らせる紙吹雪の枚数（表彰より多くして特別感を出す）。 @graph-connects none */
const CHAMPION_CONFETTI = 260;
/** 表彰で降らせる紙吹雪の枚数。 @graph-connects none */
const PODIUM_CONFETTI = 120;
/** 画面を開いたときの発表をまだ見ているか（一度でも順位が替わったら以降は札から見せる）。 @graph-connects none */
const INITIAL_FOCUS_CONSUMED = Symbol('consumed');

/** ステージ画面の props @graph-connects none */
interface StageScreenProps {
  sessionId: string;
  view: CuttysarkLiveView;
}

/** 締め切ったときのドラムロールの長さ（ms）。 @graph-connects none */
const CLOSED_DRUMROLL_MS = 2500;

/**
 * CuttySark 賞のステージ画面（Meet で画面共有する演出画面）。進行状態ごとに画面を切り替える:
 * 準備中は出航待ち、紹介・発表中は 1 人を大きく、投票中は受付の表示、締切後は集計中、
 * 結果発表中は運営がめくった順位を「第 N 位」の札 → ドラムロール → 発表の流れで 1 つずつ出し
 * （1 位は溜めを長く豪華に）、発表した人へのコメントだけを匿名で流す。終了後は表彰台。
 * 判定はせず、API が返した見え方をそのまま演出する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面（代表者紹介・発表中・投票受付・集計中・順位ごとの結果発表・表彰を全画面で演出）
 * @graph-connects revealFocus [calls] いま発表している順位
 * @graph-connects RankReveal [calls] 順位の発表
 * @graph-connects flowingComments [calls] 流すコメント
 * @graph-connects createStageSound [calls] 効果音
 */
export function StageScreen({ sessionId, view }: StageScreenProps): ReactNode {
  const { t } = useTranslation();
  const [confetti, setConfetti] = useState(0);
  const sound = useRef<StageSound | null>(null);
  const [soundOn, setSoundOn] = useState(false);

  // いま発表している順位。画面を開いた時点の発表は発表済みの状態から出し（途中参加・読み直しで
  // 毎回札から始めないため）、運営が順位を進めたら札 → ドラムロール → 発表の流れで見せる
  const focus = view.phase === 'revealing' ? revealFocus(view.results, view.reveal) : null;
  const focusKey = focus?.key ?? null;
  const initialFocusKey = useRef<string | null | typeof INITIAL_FOCUS_CONSUMED>(focusKey);
  useEffect(() => {
    if (focusKey !== initialFocusKey.current) initialFocusKey.current = INITIAL_FOCUS_CONSUMED;
  }, [focusKey]);
  // コメントは発表し終えた順位の人の分だけ流す（全員分を一度に流すと画面が重くなるため）
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  // 1 位の発表だけ、画面全体を暗転させてスポットライトを振り（溜め）、発表の瞬間に白く光らせる
  const [championEffect, setChampionEffect] = useState<'spotlight' | 'flash' | null>(null);
  const onRevealStep = useCallback(
    (
      key: string,
      step: RevealStepName,
      { champion, animated }: { champion: boolean; animated: boolean },
    ) => {
      if (step === 'drumroll') {
        if (champion) sound.current?.grandDrumroll(CHAMPION_DRUMROLL_MS);
        else sound.current?.drumroll(DRUMROLL_MS);
      }
      setChampionEffect(
        champion && step === 'drumroll'
          ? 'spotlight'
          : champion && animated && step === 'revealed'
            ? 'flash'
            : null,
      );
      // 発表し直す（戻して進め直す）ときに前のコメントが溜めの間に流れないよう、発表までは消しておく
      if (step !== 'revealed') {
        setRevealedKey(null);
        return;
      }
      setRevealedKey(key);
      if (!animated) return;
      if (champion) {
        sound.current?.grandFanfare();
        setConfetti(CHAMPION_CONFETTI);
      } else {
        sound.current?.fanfare();
      }
    },
    [],
  );
  // 代表者紹介・発表で人が変わったら登場音、投票を締め切ったら集計中のドラムロール
  const presenterKey =
    view.phase === 'intro' || view.phase === 'presenting' ? view.currentCandidateEmail : null;
  useEffect(() => {
    if (presenterKey !== null) sound.current?.entrance();
  }, [presenterKey]);
  useEffect(() => {
    if (view.phase === 'closed') sound.current?.drumroll(CLOSED_DRUMROLL_MS);
  }, [view.phase]);

  // 画面を閉じるときに AudioContext も閉じる
  useEffect(
    () => () => {
      sound.current?.close();
    },
    [],
  );

  // 終了に切り替わったら表彰の紙吹雪
  useEffect(() => {
    if (view.phase === 'finished') setConfetti(PODIUM_CONFETTI);
  }, [view.phase]);

  // 紙吹雪は一定時間で片付ける
  useEffect(() => {
    if (confetti === 0) return;
    const timer = setTimeout(() => {
      setConfetti(0);
    }, CONFETTI_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [confetti]);

  const current = view.candidates.find((c) => c.email === view.currentCandidateEmail);
  const divisionName = (id: string): string => view.divisions.find((d) => d.id === id)?.name ?? '';
  const flowing =
    focus !== null && revealedKey === focus.key
      ? flowingComments([{ divisionId: focus.divisionId, entries: focus.entries }], view.candidates)
      : [];

  /** 進行状態ごとの中身 */
  const body = (): ReactNode => {
    switch (view.phase) {
      case 'preparing':
        return (
          <div className="flex flex-col items-center gap-[3vh] text-center">
            <div className="text-[clamp(1rem,2vw,1.6rem)] tracking-[0.5em] text-blue-200">
              {view.quarter} ・ {t(`cuttysark.stage.${view.stage}`)}
            </div>
            <h1 className="cs-slide-up text-[clamp(2.4rem,7vw,6rem)] font-black text-white">
              {view.title}
            </h1>
            <div className="cs-pulse text-[clamp(1.4rem,3vw,2.4rem)] font-bold text-amber-300">
              {t('cuttysark.stageScreen.preparing')}
            </div>
          </div>
        );
      case 'intro':
      case 'presenting':
        return current ? (
          <CandidateSpotlight
            key={current.email}
            candidate={current}
            divisionName={divisionName(current.divisionId)}
            {...(view.phase === 'presenting'
              ? { banner: t('cuttysark.stageScreen.presenting') }
              : {})}
          />
        ) : (
          <CandidateGrid divisions={view.divisions} candidates={view.candidates} />
        );
      case 'voting':
        return (
          <div className="flex flex-col items-center gap-[4vh] text-center">
            <div className="cs-pulse text-[clamp(3rem,9vw,8rem)] font-black text-amber-300">
              {t('cuttysark.stageScreen.voting')}
            </div>
            <div className="text-[clamp(1.2rem,2.4vw,2rem)] text-blue-100">
              {t('cuttysark.stageScreen.votingHint')}
            </div>
            {view.operatorView && (
              <div className="text-[clamp(1.6rem,3.6vw,3rem)] font-bold text-white">
                {t('cuttysark.stageScreen.votedCount', {
                  count: view.operatorView.submittedVoters.length,
                })}
              </div>
            )}
          </div>
        );
      case 'closed':
        return (
          <div className="cs-drumroll text-[clamp(3rem,9vw,8rem)] font-black text-amber-300">
            {t('cuttysark.stageScreen.closed')}
          </div>
        );
      case 'revealing':
        return focus ? (
          <RankReveal
            key={focus.key}
            focus={focus}
            divisionName={divisionName(focus.divisionId)}
            candidates={view.candidates}
            animate={initialFocusKey.current !== focus.key}
            onStep={(step, info) => {
              onRevealStep(focus.key, step, info);
            }}
          />
        ) : (
          <div className="cs-drumroll text-[clamp(3rem,9vw,8rem)] font-black text-amber-300">
            {t('cuttysark.stageScreen.revealing')}
          </div>
        );
      case 'finished':
        return (
          <div className="flex w-full flex-col items-center gap-[3vh]">
            <div className="cs-slide-up text-[clamp(1.8rem,7vh,4rem)] font-black text-amber-300">
              {t('cuttysark.stageScreen.finished')}
            </div>
            <WinnersPodium
              divisions={view.divisions}
              candidates={view.candidates}
              results={view.results}
            />
          </div>
        );
    }
  };

  // 共有シェルの本文ラッパーは transform を持ち fixed の包含ブロックになるため、
  // body 直下へ出して画面全体を覆う（アプリのヘッダー・サイドバーを隠して画面共有する）
  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-hidden bg-slate-950 text-white">
      <StageBackdrop />
      {/* 暗転とスポットライトは本文（第 1 位は…）の下に敷く。本文より前に置くと重なり順で下になる */}
      {focus !== null && championEffect === 'spotlight' && <ChampionSpotlights />}
      <header className="absolute left-0 right-0 top-0 z-30 flex items-center justify-between px-6 py-3 text-sm text-blue-200/70">
        <span className="font-semibold tracking-[0.3em]">CUTTY SARK ・ {view.title}</span>
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="rounded-full border border-blue-200/30 px-3 py-1 hover:bg-white/10"
            aria-pressed={soundOn}
            onClick={() => {
              // ブラウザは利用者の操作なしに音を出せないので、押されたときに作る
              if (soundOn) sound.current?.close();
              sound.current = soundOn ? null : createStageSound();
              // 鳴ったことがその場で分かるよう、オンにしたら確認音を鳴らす
              sound.current?.chime();
              setSoundOn(!soundOn);
            }}
          >
            {soundOn ? t('cuttysark.stageScreen.soundOn') : t('cuttysark.stageScreen.soundOff')}
          </button>
          {/* 運営は操作盤へ、参加者は参加者画面へ戻す（参加者は操作盤を開けないため） */}
          <Link
            to={view.viewer.isOperator ? '/cuttysark/$sessionId/operate' : '/cuttysark/$sessionId'}
            params={{ sessionId }}
            className="rounded-full px-3 py-1 hover:bg-white/10"
          >
            {t('cuttysark.stageScreen.exit')}
          </Link>
        </div>
      </header>
      {/* 中身は m-auto で中央に置く。items-center で中央寄せすると、はみ出したときに上側が切れて
          スクロールでも戻れないため（はみ出したら上から並び、スクロールで下を見られる） */}
      <main className="relative z-0 flex h-full overflow-y-auto py-[7vh]">
        <div className="m-auto flex w-full justify-center">{body()}</div>
      </main>
      {/* 発表を戻したり結果発表から抜けたりしたら、閃光は残さない */}
      {focus !== null && championEffect === 'flash' && <ChampionFlash />}
      <CommentStream comments={flowing} />
      {confetti > 0 && <Confetti count={confetti} />}
    </div>,
    document.body,
  );
}
